/* MARQUE v48 — Customer-controlled product comparison.
   This is intentionally client-only, using sessionStorage. No network calls.
   Comparison shows title, clothing category and Shopify-rendered price;
   it makes no invented fit, inventory or fulfillment claims. */
(() => {
  'use strict';
  if (window.marqueCompareReady) return;
  const root = document.querySelector('[data-marque-compare-root]');
  if (!root) return;
  window.marqueCompareReady = true;

  const KEY = 'marque-compare-v48';
  const limit = 3;
  const tray = root.querySelector('[data-marque-compare-tray]');
  const count = root.querySelector('[data-marque-compare-count]');
  const dialog = root.querySelector('[data-marque-compare-dialog]');
  const list = root.querySelector('[data-marque-compare-items]');
  const empty = root.querySelector('[data-marque-compare-empty]');
  const announce = root.querySelector('[data-marque-compare-status]');
  let lastFocus = null;

  function safeProductLink(raw) {
    if (!raw || typeof raw !== 'string') return false;
    try {
      const url = new URL(raw, window.location.origin);
      return url.origin === window.location.origin &&
        /(?:^|\/)products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname);
    } catch (e) { return false; }
  }
  function safeImage(raw) {
    if (!raw || typeof raw !== 'string') return false;
    try {
      const url = new URL(raw, window.location.origin);
      return url.protocol === 'https:' &&
        (url.hostname === window.location.hostname ||
         url.hostname === 'cdn.shopify.com' ||
         url.hostname.endsWith('.shopifycdn.net'));
    } catch (e) { return false; }
  }
  function parseItem(data) {
    const id = String(data.id || '');
    if (!/^\d+$/.test(id) || !safeProductLink(data.url)) return null;
    return {
      id,
      name: String(data.name || 'MARQUE piece').slice(0,110),
      kind: String(data.kind || 'Clothing').slice(0,48),
      price: String(data.price || '').slice(0,50),
      url: new URL(data.url, window.location.origin).href,
      image: safeImage(data.image) ? data.image : ''
    };
  }
  function load() {
    try {
      const raw = JSON.parse(sessionStorage.getItem(KEY) || '[]');
      if (!Array.isArray(raw)) return [];
      const seen = new Set();
      return raw.map(parseItem).filter(item => {
        if (!item || seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      }).slice(0,limit);
    } catch(e) { return []; }
  }
  let items = load();
  function save() {
    try { sessionStorage.setItem(KEY, JSON.stringify(items)); }
    catch(e) { /* comparison remains usable for current page */ }
  }
  function element(tag, cls, content) {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    if (content != null) el.textContent = content;
    return el;
  }
  function renderCards() {
    list.replaceChildren();
    items.forEach(item => {
      const li = element('li','marque-compare-dialog__card');
      const photo = element('div','marque-compare-dialog__photo');
      if (item.image) {
        const img = element('img');
        img.src = item.image;
        img.alt = '';
        img.width = 500;
        img.height = 380;
        img.loading = 'lazy';
        img.decoding = 'async';
        photo.appendChild(img);
      } else {
        const star = element('span','marque-compare-dialog__photo-star','✦');
        star.setAttribute('aria-hidden','true');
        photo.appendChild(star);
      }
      const info = element('div','marque-compare-dialog__info');
      info.append(
        element('span','marque-compare-dialog__kind',item.kind),
        element('span','marque-compare-dialog__name',item.name),
        element('p','marque-compare-dialog__price',item.price)
      );
      const actions = element('div','marque-compare-dialog__actions');
      const link = element('a','marque-compare-dialog__view','View piece ↗');
      link.href = item.url;
      const remove = element('button','marque-compare-dialog__remove','×');
      remove.type = 'button';
      remove.dataset.marqueCompareRemove = item.id;
      remove.setAttribute('aria-label','Remove ' + item.name + ' from comparison');
      actions.append(link,remove);
      li.append(photo,info,actions);
      list.appendChild(li);
    });
    empty.hidden = items.length !== 0;
  }
  function refreshButtons() {
    document.querySelectorAll('[data-marque-compare]').forEach(btn => {
      btn.hidden = false;
      const selected = items.some(item => item.id === btn.dataset.id);
      btn.setAttribute('aria-pressed', String(selected));
      btn.setAttribute('aria-label', (selected ? 'Remove ' : 'Compare ') +
        (btn.dataset.name || 'piece') + (selected ? ' from comparison' : ''));
      const word = btn.querySelector('[data-marque-compare-word]');
      const label = selected ? 'Comparing' : 'Compare';
      if (word && word.textContent !== label) word.textContent = label;
    });
  }
  function update() {
    tray.hidden = items.length === 0;
    count.textContent = items.length + ' / ' + limit;
    refreshButtons();
    renderCards();
  }
  function say(s) { announce.textContent = s; }

  function openCompare() {
    if (typeof dialog.showModal !== 'function') {
      say('Product comparison needs a current browser.');
      return;
    }
    lastFocus = document.activeElement;
    renderCards();
    if (!dialog.open) dialog.showModal();
    dialog.querySelector('[data-marque-compare-close]')?.focus();
  }
  function closeCompare() {
    if (dialog.open) dialog.close();
  }
  dialog.addEventListener('close', () => {
    if (lastFocus instanceof HTMLElement && lastFocus.isConnected && !lastFocus.hidden) {
      lastFocus.focus();
    } else if (!tray.hidden) {
      tray.querySelector('[data-marque-compare-open]')?.focus();
    }
    lastFocus = null;
  });
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    const outside = event.clientX < box.left || event.clientX > box.right ||
      event.clientY < box.top || event.clientY > box.bottom;
    if (outside) closeCompare();
  });

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const toggle = event.target.closest('[data-marque-compare]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      const item = parseItem(toggle.dataset);
      if (!item) return;
      const index = items.findIndex(candidate => candidate.id === item.id);
      if (index >= 0) {
        items.splice(index,1);
        say(item.name + ' removed from comparison.');
      } else {
        if (items.length >= limit) {
          say('You can compare up to three pieces. Remove a piece to add another.');
          openCompare();
          return;
        }
        items.push(item);
        say(item.name + ' added to comparison. ' + items.length + ' of three pieces selected.');
      }
      save(); update();
      return;
    }
    if (event.target.closest('[data-marque-compare-open]')) {openCompare();return;}
    if (event.target.closest('[data-marque-compare-close]')) {closeCompare();return;}
    const remove = event.target.closest('[data-marque-compare-remove]');
    if (remove) {
      items = items.filter(item => item.id !== remove.dataset.marqueCompareRemove);
      save();update();say('Piece removed from comparison.');
      dialog.querySelector('[data-marque-compare-close]')?.focus();
      return;
    }
    if (event.target.closest('[data-marque-compare-clear]')) {
      items = [];save();update();say('Comparison cleared.');
    }
  });

  // Shopify collection filters/search can replace cards with Ajax responses.
  // Observe only newly added compare controls, not every DOM change.
  let scheduled = false;
  const observer = new MutationObserver(mutations => {
    const found = mutations.some(mutation => Array.from(mutation.addedNodes).some(node =>
      node.nodeType === 1 &&
      (node.matches?.('[data-marque-compare]') || node.querySelector?.('[data-marque-compare]'))
    ));
    if (!found || scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; refreshButtons(); });
  });
  observer.observe(document.body, {childList:true,subtree:true});
  document.addEventListener('shopify:section:load',refreshButtons);
  window.addEventListener('pageshow',refreshButtons);

  // When a color swatch is chosen on a MARQUE card, present only variants
  // matching that color. The underlying Shopify variant form still submits
  // the exact, existing variant ID. No custom cart API is involved.
  function syncCardColour(card, swatch) {
    if (!card || !swatch) return;
    const chosen = swatch.dataset.colourHandle || '';
    if (!chosen) return;
    const options = [...card.querySelectorAll('li[data-marque-variant-colour]')];
    if (!options.length || !options.some(li => li.dataset.marqueVariantColour === chosen)) return;
    options.forEach(li => { li.hidden = li.dataset.marqueVariantColour !== chosen; });
    const label = card.querySelector('[data-marque-selected-colour]');
    if (label) label.textContent = ' · ' + (swatch.dataset.colourName || '');
  }
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const swatch = event.target.closest('.marque-card__swatch[data-colour-handle]');
    if (!swatch) return;
    syncCardColour(swatch.closest('[data-marque-card]'), swatch);
  });
  document.addEventListener('toggle', event => {
    if (!(event.target instanceof HTMLDetailsElement) ||
        !event.target.matches('.marque-card__sizes') || !event.target.open) return;
    const card = event.target.closest('[data-marque-card]');
    syncCardColour(card,card?.querySelector('.marque-card__swatch[aria-pressed="true"][data-colour-handle]'));
  }, true);
  update();
})();
